import { Card } from "../../components/Card";
import { StatusBadge } from "../../components/StatusBadge";
import { fmtMoney } from "../../lib/format";
import type {
  ConnectedServiceDTO,
  SubscriptionDTO,
  UserDTO,
} from "@shared/api";

interface Props {
  sub: SubscriptionDTO;
  services: ConnectedServiceDTO[];
  monthlyTotal: number;
  assignedUsers: UserDTO[];
  otherSubs: SubscriptionDTO[];
  onOpenSubscription: (id: string) => void;
  setTab: (tab: "services" | "seats") => void;
}

export function Overview({
  sub,
  services,
  monthlyTotal,
  assignedUsers,
  otherSubs,
  onOpenSubscription,
  setTab,
}: Props) {
  const byId = new Map(services.map((s) => [s.id, s]));
  const enabled = sub.services
    .map((id) => byId.get(id))
    .filter((s): s is ConnectedServiceDTO => !!s);

  return (
    <div className="bbc-detail-grid">
      <Card
        title="What's included"
        action={
          <button
            type="button"
            className="bbc-text-btn"
            onClick={() => setTab("services")}
          >
            Configure →
          </button>
        }
      >
        {enabled.length === 0 ? (
          <div className="bbc-empty">No services enabled yet.</div>
        ) : (
          <ul className="bbc-incl-list">
            {enabled.map((s) => (
              <li key={s.id}>
                <div>
                  <div className="bbc-incl-name">{s.name}</div>
                  <div className="bbc-incl-desc">{s.description}</div>
                </div>
                <div className="bbc-incl-price mono">
                  {fmtMoney(s.monthly)}
                  <span className="dim">
                    /mo · {s.metric}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="bbc-incl-total">
          <span>Monthly commitment</span>
          <span className="mono">{fmtMoney(monthlyTotal)}</span>
        </div>
      </Card>

      <Card
        title="Assigned users"
        action={
          <button
            type="button"
            className="bbc-text-btn"
            onClick={() => setTab("seats")}
          >
            Manage →
          </button>
        }
      >
        {assignedUsers.length === 0 ? (
          <div className="bbc-empty">No users on this subscription yet.</div>
        ) : (
          <div className="bbc-users-stack">
            {assignedUsers.slice(0, 6).map((u) => (
              <div key={u.id} className="bbc-user-row">
                <span className="bbc-avatar bbc-avatar-md">{u.initials}</span>
                <div className="bbc-user-row-text">
                  <div>{u.name}</div>
                  <div>
                    {u.role} · {u.lastActive}
                  </div>
                </div>
              </div>
            ))}
            {assignedUsers.length > 6 && (
              <div className="bbc-user-more">
                +{assignedUsers.length - 6} more
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Switch subscription">
        {otherSubs.length === 0 ? (
          <div className="bbc-empty">No other subscriptions in this workspace.</div>
        ) : (
          <div className="bbc-switch-list">
            {otherSubs.slice(0, 5).map((s) => (
              <button
                key={s.id}
                type="button"
                className="bbc-switch-row"
                onClick={() => onOpenSubscription(s.id)}
              >
                <div>
                  <div className="bbc-switch-name">{s.name}</div>
                  <div className="bbc-switch-meta mono">
                    {s.code} · {s.domain}
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
